import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import AdminShell from "../components/AdminShell";
import { Plus, X, Search, Pencil, Trash2, Rocket, Inbox } from "lucide-react";

const TYPES = ["Pre-seed accelerator", "Pre-seed VC", "Accelerator", "Inclusive-tech accelerator", "Pan-African accelerator", "Impact accelerator", "Pre-seed collective", "Founder studio"];
const SECTORS = ["Fintech", "Agritech", "Healthtech", "Cleantech", "Logistics", "E-commerce", "Manufacturing", "Edtech", "Hospitality"];
const COUNTRIES = ["Nigeria", "Kenya", "Ghana", "South Africa", "Egypt", "Rwanda", "Senegal", "Ethiopia", "Tanzania", "Uganda", "Côte d'Ivoire", "Tunisia"];
const STAGES = ["Idea", "MVP", "Revenue", "Growth"];

const ipt = "w-full rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm text-neutral-100 outline-none focus:border-[#F59E0B]";
const blank = () => ({
  name: "", type: "Pre-seed accelerator",
  countries: [], sectors: [], stages: [],
  ticket_min: 50000, ticket_max: 250000,
  website: "", summary: "", deadline: "",
});

function MultiChips({ options, value, onChange, testid }) {
  const toggle = (v) => onChange(value.includes(v) ? value.filter(x => x !== v) : [...value, v]);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {options.map(o => (
        <button key={o} type="button" onClick={() => toggle(o)}
          className={"rounded-full border px-2.5 py-0.5 text-[11px] font-semibold transition-colors " +
            (value.includes(o) ? "border-[#F59E0B] bg-[#F59E0B]/20 text-[#F59E0B]"
                               : "border-neutral-800 bg-neutral-950 text-neutral-400 hover:border-neutral-700")}>
          {o}
        </button>
      ))}
    </div>
  );
}

export default function AdminAccelerators() {
  const [tab, setTab] = useState("directory"); // "directory" | "applications"
  const [rows, setRows] = useState([]);
  const [apps, setApps] = useState([]);
  const [appsFilter, setAppsFilter] = useState("");
  const [filter, setFilter] = useState({ q: "", country: "", sector: "" });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    const params = {};
    if (filter.country) params.country = filter.country;
    if (filter.sector) params.sector = filter.sector;
    if (filter.q) params.q = filter.q;
    const { data } = await api.get("/admin/accelerators", { params });
    setRows(data || []);
  }, [filter]);
  const loadApps = useCallback(async () => {
    const params = appsFilter ? { accelerator_id: appsFilter } : {};
    const { data } = await api.get("/admin/accelerators/applications", { params });
    setApps(data || []);
  }, [appsFilter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === "applications") loadApps(); }, [tab, loadApps]);

  const openNew = () => { setEditing(null); setForm(blank()); setErr(""); setModalOpen(true); };
  const openEdit = (a) => {
    setEditing(a.accelerator_id);
    setForm({
      name: a.name || "", type: a.type || "Pre-seed accelerator",
      countries: a.countries || [], sectors: a.sectors || [], stages: a.stages || [],
      ticket_min: a.ticket_min || 0, ticket_max: a.ticket_max || 0,
      website: a.website || "", summary: a.summary || "", deadline: a.deadline || "",
    });
    setErr(""); setModalOpen(true);
  };
  const save = async () => {
    if (!form.name.trim()) { setErr("Name required"); return; }
    setBusy(true); setErr("");
    try {
      const payload = { ...form, ticket_min: Number(form.ticket_min) || 0, ticket_max: Number(form.ticket_max) || 0 };
      if (editing) await api.put(`/admin/accelerators/${editing}`, payload);
      else await api.post(`/admin/accelerators`, payload);
      setModalOpen(false);
      await load();
    } catch (e) { setErr(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this accelerator?")) return;
    await api.delete(`/admin/accelerators/${id}`);
    await load();
  };

  return (
    <AdminShell>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Rocket className="h-5 w-5 text-[#F59E0B]"/>
            <h1 className="text-3xl font-bold">Accelerator directory</h1>
          </div>
          <p className="mt-1 text-sm text-neutral-400">Africa-based pre-seed accelerators surfaced to founders on the pre-revenue / early-revenue pathway.</p>
        </div>
        <button onClick={openNew} data-testid="admin-acc-new"
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#F59E0B] px-4 py-2 text-sm font-bold text-neutral-950 hover:bg-[#EAB308]">
          <Plus className="h-4 w-4"/> Add accelerator
        </button>
      </div>

      <div className="mt-6 flex gap-1 border-b border-neutral-800" data-testid="admin-acc-tabs">
        <button onClick={() => setTab("directory")} data-testid="admin-acc-tab-directory"
          className={"px-4 py-2 text-sm font-semibold transition-colors " +
            (tab === "directory" ? "border-b-2 border-[#F59E0B] text-[#F59E0B]"
                                  : "text-neutral-400 hover:text-neutral-200")}>
          Directory ({rows.length})
        </button>
        <button onClick={() => setTab("applications")} data-testid="admin-acc-tab-applications"
          className={"inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold transition-colors " +
            (tab === "applications" ? "border-b-2 border-[#F59E0B] text-[#F59E0B]"
                                     : "text-neutral-400 hover:text-neutral-200")}>
          <Inbox className="h-4 w-4"/> Applications
        </button>
      </div>

      {tab === "directory" && (<>
      <div className="mt-6 grid grid-cols-1 gap-2 md:grid-cols-3">
        <div className="flex items-center gap-2 rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-2">
          <Search className="h-4 w-4 text-neutral-500"/>
          <input data-testid="admin-acc-search" value={filter.q} onChange={e => setFilter({...filter, q: e.target.value})}
            placeholder="Search by name" className="w-full bg-transparent text-sm outline-none placeholder:text-neutral-600"/>
        </div>
        <select value={filter.country} onChange={e => setFilter({...filter, country: e.target.value})} className={ipt}>
          <option value="">All countries</option>{COUNTRIES.map(c => <option key={c}>{c}</option>)}
        </select>
        <select value={filter.sector} onChange={e => setFilter({...filter, sector: e.target.value})} className={ipt}>
          <option value="">All sectors</option>{SECTORS.map(s => <option key={s}>{s}</option>)}
        </select>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-neutral-800 bg-neutral-900">
        <table className="w-full text-sm" data-testid="admin-acc-table">
          <thead className="border-b border-neutral-800 bg-neutral-950 text-left text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-4 py-3 font-semibold">Name</th>
              <th className="px-4 py-3 font-semibold">Type</th>
              <th className="px-4 py-3 font-semibold">Countries</th>
              <th className="px-4 py-3 font-semibold">Ticket</th>
              <th className="px-4 py-3 font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-neutral-500">No accelerators yet.</td></tr>}
            {rows.map(r => (
              <tr key={r.accelerator_id} className="border-b border-neutral-800 hover:bg-neutral-800/40" data-testid={`admin-acc-row-${r.accelerator_id}`}>
                <td className="px-4 py-3">
                  <p className="font-semibold text-neutral-100">{r.name}</p>
                  <p className="text-xs text-neutral-500 line-clamp-1">{r.summary}</p>
                </td>
                <td className="px-4 py-3"><span className="rounded bg-[#F59E0B]/15 px-2 py-0.5 text-[11px] font-bold text-[#F59E0B]">{r.type}</span></td>
                <td className="px-4 py-3 text-neutral-300">{(r.countries || []).slice(0, 3).join(", ")}</td>
                <td className="px-4 py-3 text-neutral-300">${((r.ticket_min || 0)/1000).toFixed(0)}K–${((r.ticket_max || 0)/1000).toFixed(0)}K</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => openEdit(r)} className="rounded p-1.5 text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100"><Pencil className="h-4 w-4"/></button>
                    <button onClick={() => remove(r.accelerator_id)} className="rounded p-1.5 text-red-400 hover:bg-red-950/40"><Trash2 className="h-4 w-4"/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>)}

      {tab === "applications" && (
        <div className="mt-6">
          <div className="mb-3 flex items-center gap-2">
            <select value={appsFilter} onChange={e => setAppsFilter(e.target.value)} className={ipt + " max-w-xs"} data-testid="admin-acc-apps-filter">
              <option value="">All accelerators</option>
              {rows.map(r => <option key={r.accelerator_id} value={r.accelerator_id}>{r.name}</option>)}
            </select>
            <span className="text-sm text-neutral-500">{apps.length} application{apps.length === 1 ? "" : "s"}</span>
          </div>
          <div className="overflow-hidden rounded-2xl border border-neutral-800 bg-neutral-900">
            <table className="w-full text-sm" data-testid="admin-acc-apps-table">
              <thead className="border-b border-neutral-800 bg-neutral-950 text-left text-xs uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="px-4 py-3 font-semibold">Company</th>
                  <th className="px-4 py-3 font-semibold">Accelerator</th>
                  <th className="px-4 py-3 font-semibold">Applicant</th>
                  <th className="px-4 py-3 font-semibold">Sector / Country</th>
                  <th className="px-4 py-3 font-semibold">Submitted</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {apps.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-neutral-500">No applications yet.</td></tr>
                )}
                {apps.map(a => (
                  <tr key={a.application_id} className="border-b border-neutral-800 hover:bg-neutral-800/40" data-testid={`admin-acc-app-row-${a.application_id}`}>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-neutral-100">{a.company_name}</p>
                      <p className="text-xs text-neutral-500 line-clamp-1">{a.value_prop}</p>
                    </td>
                    <td className="px-4 py-3 text-neutral-300">{a.accelerator_name}</td>
                    <td className="px-4 py-3">
                      <p className="text-neutral-200">{a.applicant_name}</p>
                      <p className="text-xs text-neutral-500">{a.applicant_email}</p>
                    </td>
                    <td className="px-4 py-3 text-neutral-300">{a.sector || "—"} / {a.country || "—"}</td>
                    <td className="px-4 py-3 text-xs text-neutral-500">{a.created_at?.slice(0, 16).replace("T", " ")}</td>
                    <td className="px-4 py-3"><span className="rounded bg-[#F59E0B]/15 px-2 py-0.5 text-[11px] font-bold uppercase text-[#F59E0B]">{a.status || "submitted"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="admin-acc-modal">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-neutral-800 bg-neutral-950 p-6">
            <div className="flex items-start justify-between">
              <h3 className="text-lg font-bold">{editing ? "Edit accelerator" : "Add accelerator"}</h3>
              <button onClick={() => setModalOpen(false)} className="rounded-md p-1 text-neutral-400 hover:bg-neutral-800"><X className="h-5 w-5"/></button>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="md:col-span-2"><Label>Name</Label><input data-testid="admin-acc-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className={ipt+" mt-1"}/></div>
              <div><Label>Type</Label><select value={form.type} onChange={e => setForm({...form, type: e.target.value})} className={ipt+" mt-1"}>{TYPES.map(t => <option key={t}>{t}</option>)}</select></div>
              <div><Label>Website</Label><input value={form.website} onChange={e => setForm({...form, website: e.target.value})} className={ipt+" mt-1"}/></div>
              <div><Label>Ticket min</Label><input type="number" data-testid="admin-acc-tmin" value={form.ticket_min} onChange={e => setForm({...form, ticket_min: e.target.value})} className={ipt+" mt-1"}/></div>
              <div><Label>Ticket max</Label><input type="number" data-testid="admin-acc-tmax" value={form.ticket_max} onChange={e => setForm({...form, ticket_max: e.target.value})} className={ipt+" mt-1"}/></div>
              <div className="md:col-span-2"><Label>Sectors</Label><div className="mt-1"><MultiChips options={SECTORS} value={form.sectors} onChange={v => setForm({...form, sectors: v})}/></div></div>
              <div className="md:col-span-2"><Label>Countries</Label><div className="mt-1"><MultiChips options={COUNTRIES} value={form.countries} onChange={v => setForm({...form, countries: v})}/></div></div>
              <div className="md:col-span-2"><Label>Stages</Label><div className="mt-1"><MultiChips options={STAGES} value={form.stages} onChange={v => setForm({...form, stages: v})}/></div></div>
              <div className="md:col-span-2"><Label>Deadline</Label><input value={form.deadline} onChange={e => setForm({...form, deadline: e.target.value})} placeholder="Rolling / Quarterly cohorts / Annual" className={ipt+" mt-1"}/></div>
              <div className="md:col-span-2"><Label>Summary</Label><textarea rows={3} value={form.summary} onChange={e => setForm({...form, summary: e.target.value})} className={ipt+" mt-1"}/></div>
            </div>
            {err && <p className="mt-3 text-sm text-red-400">{String(err)}</p>}
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setModalOpen(false)} className="rounded-lg border border-neutral-800 bg-neutral-900 px-4 py-2 text-sm font-semibold text-neutral-300 hover:bg-neutral-800">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="admin-acc-save"
                className="rounded-lg bg-[#F59E0B] px-5 py-2 text-sm font-bold text-neutral-950 hover:bg-[#EAB308] disabled:opacity-50">
                {busy ? "Saving…" : "Save accelerator"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}

const Label = ({children}) => <label className="text-xs font-semibold uppercase tracking-wider text-neutral-400">{children}</label>;
